import { ReactNode } from "react";
import { StyleSheet, css } from "aphrodite"
import { backgroundColor, darkAmberColor, primaryColor, shadowColor, softBlack } from "../theme"
import React from "react";
import commonCss, { mobileView, padding, width } from "../commonCss/commonCss";
import { MdDeleteOutline, MdOutlineAdd, MdOutlineFileDownload } from "react-icons/md";
import { FiEdit } from "react-icons/fi";
import { FaGoogle, FaPhoneAlt, FaWhatsapp } from "react-icons/fa";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    text: string,
    color?: string,
    size?: string,
}

export const ButtonMedium = React.memo(({ text, onClick, color, size, className, disabled }: ButtonProps) => {
    return <button onClick={onClick} disabled={disabled} className={css(styles.buttonMedium, width(size || "5rem"), DynamicColorCSS(color)) + (className ? ` ${className}` : "")}>
        {text}
    </button>
});

export const AddButton = React.memo(({ onClick, className }: React.HTMLAttributes<HTMLButtonElement>) => {
    return <button className={css(commonCss.buttonShadow, commonCss.marginLR15px, padding('0.2rem')) + (className ? ` ${className}` : "")}
        style={{
            backgroundColor: 'white',
            borderRadius: '1rem',
            width: '5rem',
        }}
        title="Add"
        onClick={onClick}>
        <MdOutlineAdd className={css(styles.buttonIcon)} color={darkAmberColor} size='1.3rem' />
    </button>
});

export const DeleteButton = React.memo(({ onClick, className, title }: React.HTMLAttributes<HTMLButtonElement>) => {
    return <button style={{
        border: 'none',
        backgroundColor: 'white',
        display: 'flex',
        alignItems: 'center',
        borderRadius: '1rem',
    }}
        onClick={onClick}
        className={css(commonCss.buttonShadow, padding('0.3rem')) + (className ? ` ${className}` : "")}
        title={title ? title : "Delete"}>
        <MdDeleteOutline className={css(styles.buttonIcon)} color={darkAmberColor} size='1.3rem' />
    </button>
});

export const DownloadButton = React.memo(({ onClick, className, title }: React.HTMLAttributes<HTMLButtonElement>) => {
    return <button style={{
        border: 'none',
        backgroundColor: 'white',
        display: 'flex',
        alignItems: 'center',
        borderRadius: '1rem',
    }}
        onClick={onClick}
        className={css(commonCss.buttonShadow, padding('0.3rem')) + (className ? ` ${className}` : "")}
        title={title ? title : "Downlaod"}>
        <MdOutlineFileDownload className={css(styles.buttonIcon)} color={darkAmberColor} size='1.5rem' />
    </button>
});

export const EditButton = React.memo(({ onClick, className, title }: React.HTMLAttributes<HTMLButtonElement>) => {
    return <button style={{
        border: 'none',
        backgroundColor: 'white',
        display: 'flex',
        alignItems: 'center',
        borderRadius: '1rem'
    }}
        onClick={onClick}
        className={css(commonCss.buttonShadow, padding('0.4rem')) + (className ? ` ${className}` : "")}
        title={title ? title : "Edit"}>
        <FiEdit className={css(styles.buttonIcon)} color={darkAmberColor} size='1.1rem' />
    </button>
});

export const WhatsappButton = React.memo(({ onClick, className, title, disabled }: React.HTMLAttributes<HTMLButtonElement> & { disabled?: boolean }) => {
    return <button style={{
        border: 'none',
        backgroundColor: 'white',
        display: 'flex',
        alignItems: 'center',
        borderRadius: '1rem',
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
    }}
        onClick={onClick}
        disabled={disabled}
        className={css(commonCss.buttonShadow, padding('0.3rem')) + (className ? ` ${className}` : "")}
        title={title ? title : "Send via WhatsApp"}>
        <FaWhatsapp className={css(styles.buttonIcon)} color={darkAmberColor} size='1.3rem' />
    </button>;
});

export const GoogleLoginButton = React.memo(({ onClick, className, title }: React.HTMLAttributes<HTMLButtonElement>) => {
    return <button
        className={css(styles.button, styles.loginButton, commonCss.boxShadow)}
        onClick={onClick}
        title={title ? title : "Continue with Google"}>
        <FaGoogle size={'1.1rem'} />
        Continue with Google
    </button>
});


export const LoginWithPhoneButton = React.memo(({ onClick, className, title }: React.HTMLAttributes<HTMLButtonElement>) => {
    return <button
        className={css(styles.button, styles.loginButton, commonCss.boxShadow)}
        onClick={onClick}
        title={title ? title : "Continue with Phone Number"}>
        <FaPhoneAlt size={'1.1rem'} />
        Continue with Phone Number
    </button>
});

export const Button = React.memo(({ children, onClick, style, title, className }: React.HTMLAttributes<HTMLButtonElement>) => {
    return <button
        className={css(styles.button, commonCss.buttonShadow) + (className ? ` ${className}` : "")}
        onClick={onClick}
        style={style}
        title={title ? title : undefined}>
        {children}
    </button>
});

const styles = StyleSheet.create({
    buttonMedium: {
        display: "block",
        padding: "1rem",
        border: "none",
        boxShadow: `1px 1px 5px ${shadowColor}`,
        ":active": {
            boxShadow: `0px 0px 1px ${shadowColor}`,
        }
    },
    buttonIcon: {
        backgroundColor: 'white',
        transitionDuration: '0.3s',
        padding: '0.2rem',
        ':hover': { color: softBlack },
    },
    loginButton: {
        color: darkAmberColor,
        ':hover': {
            color: softBlack,
        },
        ':active': {
            color: darkAmberColor,
        }
    },
    button: {
        padding: '1rem',
        borderRadius: '2rem',
        border: 'none',
        display: 'flex',
        columnGap: '1rem',
        justifyContent: 'space-evenly',
        alignItems: 'center',
        transitionDuration: '0.3s',
        backgroundColor: 'white',
    }

})

const DynamicColorCSS = (val?: string) => val ? StyleSheet.create({
    bgColor: {
        background: val
    }
}).bgColor : null;


/** Rounded call-to-action button: `primary` for the main action, `dark` for the second, `text` for a quiet link-like action. */
export function PillButton({
    variant,
    icon,
    children,
    onClick,
}: {
    variant: "primary" | "dark" | "text";
    icon?: ReactNode;
    children: ReactNode;
    onClick: () => void;
}) {
    return (
        <button type="button" onClick={onClick} className={css(pillStyles.base, pillStyles[variant])}>
            {icon}
            <span>{children}</span>
        </button>
    );
}

const pillStyles = StyleSheet.create({
    base: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        minHeight: "52px",
        padding: "0 24px",
        border: "none",
        borderRadius: "999px",
        fontFamily: "inherit",
        fontSize: "1rem",
        fontWeight: 600,
        whiteSpace: "nowrap",
        cursor: "pointer",
        transition: "background-color 0.2s",
        ":focus-visible": {
            outline: `2px solid ${softBlack}`,
            outlineOffset: "2px",
        },
        [mobileView]: {
            padding: "0 12px",
            fontSize: "0.95rem",
        },
    },
    primary: {
        backgroundColor: primaryColor,
        color: softBlack,
        ":hover": {
            backgroundColor: darkAmberColor,
        },
    },
    dark: {
        backgroundColor: softBlack,
        color: backgroundColor,
        ":hover": {
            backgroundColor: shadowColor,
        },
    },
    text: {
        minHeight: "44px",
        padding: 0,
        backgroundColor: "transparent",
        color: softBlack,
        fontSize: "0.9rem",
        textDecoration: "underline",
        textDecorationColor: darkAmberColor,
        textUnderlineOffset: "5px",
        ":hover": {
            color: shadowColor,
        },
        [mobileView]: {
            padding: 0,
        },
    },
});